// Полноэкранная шторка диктовки (M9).
//
// Запись и расшифровка — тот же путь, что у полосы записи на десктопе
// (`panes/VoiceBar.tsx`): MediaRecorder → Blob → `api.voice(sessionId, blob)`,
// состояние записи в срезе сессии (`logStore.voice`), те же 28 полос уровня и
// те же тексты отказов микрофона. Второго транспорта, второго endpoint'а и
// второго набора состояний телефон не заводит.
//
// Своя рисовка здесь потому, что M9.1 требует не полосу между логом и вводом,
// а нижнюю шторку во весь низ экрана: «Слушаю», цель записи, столбики уровня,
// таймер и пара кнопок 56 px. Полоса десктопа в эту раскладку не переносится,
// а логика записи — переносится один в один.
//
// M9.3: расшифровка никогда не пишется в файл. Она уходит наверх через
// `onText`, и что с ней делать — решает вызвавший экран. Исключение — реплика
// в беседу (`autosend`): её отправляет агенту сам сервер (lib/voiceUpload.ts).

import { useCallback, useEffect, useRef } from 'react'
import { api } from '../api'
import { acquireMic } from '../lib/micBusy'
import { emptyVoiceReason } from '../lib/voiceHints'
import { sendVoice } from '../lib/voiceUpload'
import { useLogStore, usePane, VOICE_BARS } from '../store/logStore'
import { useEscapeLayer } from './Sheet'
import './dictation.css'

/** Обновление эквалайзера — раз в 260 мс, как в дизайне и как на десктопе. */
const WAVE_MS = 260
const BAR_MIN = 3
const BAR_MAX = 18
/** Высота столбика на телефоне: 3..18 из стора × масштаб = 9..54 px. */
const BAR_SCALE = 3

/** Цель записи: правка заметки, реплика в шторку агента, реплика в беседу. */
export type DictationTarget = 'note' | 'agent' | 'chat'

const TARGET_LABEL: Record<DictationTarget, string> = {
  note: 'правка заметки — уйдёт агенту предложением, в файл не пишется',
  agent: 'реплика агенту о заметке',
  chat: 'реплика в беседу',
}

interface Rec {
  recorder: MediaRecorder
  media: MediaStream
  ctx: AudioContext | null
  analyser: AnalyserNode | null
  chunks: Blob[]
  startedAt: number
  tick: ReturnType<typeof setInterval>
  wave: ReturnType<typeof setInterval>
  /** отпустить «микрофон занят» (lib/micBusy); повторный вызов безвреден */
  mic: () => void
}

interface Props {
  open: boolean
  target: DictationTarget
  sessionId: string | null
  onClose(): void
  onText(text: string, voiceId?: string | null): void
  /** Реплика в беседу: расшифровку агенту отправит сервер, шторка закрывается
   *  сразу после «отправить» — ждать whisper на экране не нужно. `onText`
   *  тогда зовётся только со старым сервером, не знающим autosend. */
  autosend?: boolean
}

function mmss(sec: number): string {
  const s = Math.max(0, Math.floor(sec))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** Понятная причина отказа микрофона вместо пустой шторки — тексты те же,
 *  что на десктопе (FR-25). */
function micError(err: unknown): string {
  const name = (err as { name?: string } | null)?.name ?? ''
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'доступ к микрофону запрещён — разрешите его в настройках браузера для этого сайта'
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'микрофон не найден — подключите устройство записи'
  }
  if (name === 'NotReadableError') {
    return 'микрофон занят другой программой'
  }
  const msg = (err as { message?: string } | null)?.message
  return msg ? `не удалось начать запись: ${msg}` : 'не удалось начать запись'
}

export default function DictationSheet({
  open, target, sessionId, onClose, onText, autosend = false,
}: Props) {
  // Пустой ключ отдаёт стабильный пустой срез: без сессии писать некуда,
  // но хуки обязаны вызываться в том же порядке.
  const key = sessionId ?? ''
  const phase = usePane(key, (s) => s.voice.phase)
  const seconds = usePane(key, (s) => s.voice.seconds)
  const bars = usePane(key, (s) => s.voice.bars)
  const error = usePane(key, (s) => s.voice.error)
  const patchVoice = useLogStore((s) => s.patchVoice)
  const resetVoice = useLogStore((s) => s.resetVoice)

  const recRef = useRef<Rec | null>(null)
  const barsRef = useRef<number[]>(new Array(VOICE_BARS).fill(BAR_MIN))
  const startedRef = useRef(false)
  // onText в замыкании эффекта размонтирования протух бы; держим свежим.
  const textRef = useRef(onText)
  textRef.current = onText

  /** Погасить железо: таймеры, дорожки, аудиоконтекст. */
  const teardown = useCallback((): Rec | null => {
    const rec = recRef.current
    recRef.current = null
    if (!rec) return null
    clearInterval(rec.tick)
    clearInterval(rec.wave)
    // запись кончилась (отправка, отмена, сбой старта) — оркестратор может слушать
    rec.mic()
    return rec
  }, [])

  const release = useCallback((rec: Rec) => {
    rec.media.getTracks().forEach((t) => t.stop())
    if (rec.ctx) void rec.ctx.close().catch(() => undefined)
  }, [])

  const upload = useCallback(async (blob: Blob) => {
    if (!sessionId) return
    try {
      const res = await api.voice(sessionId, blob)
      const text = (res.text ?? '').trim()
      if (!text) {
        patchVoice(sessionId, { phase: 'error', error: emptyVoiceReason(res, 0) })
        return
      }
      resetVoice(sessionId)
      // Отдаём расшифровку наверх и закрываемся. В тело заметки шторка не
      // пишет ничего и никогда (M9.3).
      textRef.current(text, res.voiceId)
      onClose()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'не удалось расшифровать запись'
      patchVoice(sessionId, { phase: 'error', error: msg })
    }
  }, [onClose, patchVoice, resetVoice, sessionId])

  /** Завершить запись: send=false — отмена, тогда наверх не уходит ничего. */
  const finish = useCallback((send: boolean) => {
    startedRef.current = false
    const rec = teardown()
    if (!rec) {
      if (sessionId) resetVoice(sessionId)
      if (!send) onClose()
      return
    }
    const done = () => {
      release(rec)
      if (!send || !sessionId) {
        if (sessionId) resetVoice(sessionId)
        onClose()
        return
      }
      const blob = new Blob(rec.chunks, { type: rec.recorder.mimeType || 'audio/webm' })
      if (blob.size === 0) {
        patchVoice(sessionId, { phase: 'error', error: 'запись пустая — микрофон ничего не дал' })
        return
      }
      if (autosend) {
        resetVoice(sessionId)
        sendVoice(sessionId, blob, 0, (text, _sec, voiceId) => textRef.current(text, voiceId))
        onClose()
        return
      }
      void upload(blob)
    }
    if (send && sessionId) patchVoice(sessionId, { phase: 'sending' })
    if (rec.recorder.state !== 'inactive') {
      rec.recorder.onstop = done
      try {
        rec.recorder.stop()
      } catch {
        done()
      }
    } else {
      done()
    }
  }, [autosend, onClose, patchVoice, release, resetVoice, sessionId, teardown, upload])

  // Escape = «отмена», но только пока диктовка сверху: раньше своего обработчика
  // здесь не было вовсе, и нажатие уходило шторке агента, под которой открыта
  // диктовка, — закрывались обе разом. Во время расшифровки Escape молчит, как
  // молчит и кнопка отмены: запись уже ушла на сервер.
  useEscapeLayer(open, () => { if (phase !== 'sending') finish(false) })

  // Открытие шторки = просьба начать запись. Отдельная кнопка «начать» здесь
  // была бы лишним касанием: шторку открывают, уже собравшись говорить.
  useEffect(() => {
    if (!open || !sessionId) return
    if (phase !== 'idle') return
    patchVoice(sessionId, {
      phase: 'requesting', seconds: 0, error: null, bars: new Array(VOICE_BARS).fill(BAR_MIN),
    })
  }, [open, phase, patchVoice, sessionId])

  // Старт записи — ровно один раз на фазу «requesting» (как в VoiceBar).
  useEffect(() => {
    if (!open || !sessionId) return
    if (phase !== 'requesting' || startedRef.current) return
    startedRef.current = true
    let cancelled = false

    const fail = (message: string) => {
      patchVoice(sessionId, { phase: 'error', error: message })
    }

    const run = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        fail('браузер не даёт доступ к микрофону — нужен https или localhost')
        return
      }
      if (typeof MediaRecorder === 'undefined') {
        fail('браузер не умеет запись звука (MediaRecorder недоступен)')
        return
      }
      // «Микрофон занят» — уже на запросе доступа: голосовой оркестратор глохнет
      // до первого слова. Отпускаем на каждом выходе до создания Rec, дальше — teardown.
      const mic = acquireMic()
      let media: MediaStream
      try {
        media = await navigator.mediaDevices.getUserMedia({ audio: true })
      } catch (err) {
        mic()
        fail(micError(err))
        return
      }
      if (cancelled) {
        mic()
        media.getTracks().forEach((t) => t.stop())
        return
      }

      let recorder: MediaRecorder
      try {
        recorder = new MediaRecorder(media)
      } catch (err) {
        mic()
        media.getTracks().forEach((t) => t.stop())
        fail(micError(err))
        return
      }

      // анализатор уровня — честная волна, а не рандом
      let ctx: AudioContext | null = null
      let analyser: AnalyserNode | null = null
      const AC: typeof AudioContext | undefined = window.AudioContext
        ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (AC) {
        try {
          ctx = new AC()
          analyser = ctx.createAnalyser()
          analyser.fftSize = 512
          ctx.createMediaStreamSource(media).connect(analyser)
        } catch {
          ctx = null
          analyser = null
        }
      }

      const chunks: Blob[] = []
      recorder.ondataavailable = (ev: BlobEvent) => {
        if (ev.data && ev.data.size > 0) chunks.push(ev.data)
      }

      const startedAt = Date.now()
      barsRef.current = new Array(VOICE_BARS).fill(BAR_MIN)

      const tick = setInterval(() => {
        patchVoice(sessionId, { seconds: Math.floor((Date.now() - startedAt) / 1000) })
      }, 1000)

      const wave = setInterval(() => {
        let level = BAR_MIN
        if (analyser) {
          const data = new Uint8Array(analyser.fftSize)
          analyser.getByteTimeDomainData(data)
          let sum = 0
          for (let i = 0; i < data.length; i++) {
            const v = (data[i] - 128) / 128
            sum += v * v
          }
          const rms = Math.sqrt(sum / data.length)
          level = Math.min(BAR_MAX, BAR_MIN + rms * 70)
        }
        const next = barsRef.current.slice(1)
        next.push(Math.round(level))
        barsRef.current = next
        patchVoice(sessionId, { bars: next })
      }, WAVE_MS)

      const rec: Rec = { recorder, media, ctx, analyser, chunks, startedAt, tick, wave, mic }
      recRef.current = rec
      try {
        recorder.start(250)
      } catch (err) {
        teardown()
        release(rec)
        fail(micError(err))
        return
      }
      patchVoice(sessionId, { phase: 'recording', seconds: 0, error: null })
    }

    void run()
    return () => { cancelled = true }
  }, [open, phase, patchVoice, release, sessionId, teardown])

  // Шторку закрыли снаружи (кнопкой «назад», сменой экрана) — микрофон обязан
  // погаснуть: индикатор записи в статус-баре телефона висел бы бесконечно.
  useEffect(() => {
    if (open) return
    startedRef.current = false
    const rec = teardown()
    if (rec) {
      try {
        if (rec.recorder.state !== 'inactive') {
          rec.recorder.onstop = null
          rec.recorder.stop()
        }
      } catch { /* уже остановлен */ }
      release(rec)
    }
    if (sessionId && phase !== 'idle') resetVoice(sessionId)
  }, [open, phase, release, resetVoice, sessionId, teardown])

  // Размонтирование во время записи — молча всё погасить.
  useEffect(() => () => {
    const rec = recRef.current
    recRef.current = null
    if (!rec) return
    clearInterval(rec.tick)
    clearInterval(rec.wave)
    rec.mic()
    try {
      if (rec.recorder.state !== 'inactive') {
        rec.recorder.onstop = null
        rec.recorder.stop()
      }
    } catch { /* уже остановлен */ }
    rec.media.getTracks().forEach((t) => t.stop())
    if (rec.ctx) void rec.ctx.close().catch(() => undefined)
  }, [])

  if (!open) return null

  const sending = phase === 'sending'
  const title = !sessionId ? 'Диктовать некуда'
    : phase === 'error' ? 'Не записалось'
      : phase === 'requesting' ? 'Жду микрофон'
        : sending ? 'Расшифровываю' : 'Слушаю'

  return (
    <div className="ab-dict">
      {/* Затемнение своё, а не общее у Sheet: диктовка перекрывает и шторку
          агента, и её затемнение тоже. */}
      <div
        className="ab-dict__scrim"
        onClick={() => { if (!sending) finish(false) }}
      />

      <div className="ab-dict__panel" role="dialog" aria-label="диктовка">
        <div className="ab-dict__titles">
          <span className="ab-dict__title">{title}</span>
          <span className="ab-dict__target">
            {sessionId
              ? TARGET_LABEL[target]
              : 'у заметки нет беседы — сперва отдайте её агенту'}
          </span>
        </div>

        {phase === 'error' && error ? (
          <div className="ab-dict__fail">{error}</div>
        ) : (
          <div className="ab-dict__eq" aria-hidden>
            {bars.map((h, i) => (
              <div
                className="ab-dict__bar"
                key={i}
                style={{ height: `${Math.max(BAR_MIN, h) * BAR_SCALE}px` }}
              />
            ))}
          </div>
        )}

        <div className="ab-dict__timer">{mmss(seconds)}</div>

        <div className="ab-dict__row">
          <button
            type="button"
            className="ab-dict__cancel"
            disabled={sending}
            onClick={() => finish(false)}
          >
            отмена
          </button>
          <button
            type="button"
            className="ab-dict__send"
            disabled={sending || phase !== 'recording'}
            onClick={() => finish(true)}
          >
            {sending ? 'расшифровываю…' : 'Отправить агенту'}
          </button>
        </div>
      </div>
    </div>
  )
}
